import { useState, type MouseEvent } from 'react';
import { Button, Menu, MenuItem, Checkbox, ListItemText, ListItemIcon, Divider } from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useTranslation } from 'react-i18next';
import type { SortFilterColumn } from './useTableSortFilter';

interface PmColumnPickerProps<T> {
  /** Ein-/ausblendbare Spalten (typ. sf.hideableColumns). */
  columns: SortFilterColumn<T>[];
  isVisible: (key: string) => boolean;
  onToggle: (key: string) => void;
}

/**
 * Spalten-Ein/-Ausblenden fuer das PM-Tabellen-Template. Button + Menu mit einer
 * Checkbox je ein-/ausblendbarer Spalte. Zustand und Persistenz liegen in
 * useTableSortFilter (localStorage) — diese Komponente ist rein praesentational.
 */
export function PmColumnPicker<T>({ columns, isVisible, onToggle }: PmColumnPickerProps<T>) {
  const { t } = useTranslation('common');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const visibleCount = columns.filter((c) => isVisible(c.key)).length;

  const open = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const close = () => setAnchor(null);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ViewColumnIcon fontSize="small" />}
        onClick={open}
        sx={{ color: 'var(--pm-fg-muted)', borderColor: 'var(--pm-border)' }}
      >
        {t('table.columns', 'Spalten')}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        {columns.map((col) => {
          const checked = isVisible(col.key);
          // letzte sichtbare Spalte nicht abwaehlbar — sonst leere Tabelle
          const lockLast = checked && visibleCount <= 1;
          return (
            <MenuItem
              key={col.key}
              dense
              disabled={lockLast}
              onClick={() => onToggle(col.key)}
            >
              <ListItemIcon sx={{ minWidth: 0 }}>
                <Checkbox edge="start" size="small" checked={checked} tabIndex={-1} disableRipple />
              </ListItemIcon>
              <ListItemText primary={col.label} />
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem dense onClick={close} sx={{ color: 'var(--pm-fg-subtle)', justifyContent: 'center' }}>
          {t('actions.close', 'Schließen')}
        </MenuItem>
      </Menu>
    </>
  );
}
